import {
  parseIdentitySummary,
  type IdentitySummaryV1,
} from "../identityContracts";

export const MAX_VISIBLE_IDENTITY_SUMMARIES = 8;

export type IdentitySectionState =
  | "ready"
  | "empty"
  | "pending"
  | "awaiting_confirmation"
  | "partial"
  | "failed_safe"
  | "not_applicable"
  | "legacy"
  | "deleted"
  | "unavailable"
  | "unknown"
  | "invalid";

export type IdentitySectionModel = {
  state: IdentitySectionState;
  summary: IdentitySummaryV1 | null;
  visibleSubjects: IdentitySummaryV1["subjects"];
  omittedSubjectCount: number;
  safeReasons: string[];
};

const SAFE_REASON_CODE = /^[a-z][a-z0-9_]{0,63}$/;

const resolutionPriority: Record<string, number> = {
  abstained: 0,
  provisional: 1,
  unknown: 2,
  operator_corrected: 3,
  user_confirmed: 4,
  resolved: 5,
};

const confidencePriority: Record<string, number> = {
  rejected: 0,
  low: 1,
  unknown: 2,
  medium: 3,
  high: 4,
};

function stateForSummary(summary: IdentitySummaryV1): IdentitySectionState {
  if (summary.manifest_status === "failed_safe") return "failed_safe";
  if (
    summary.manifest_status === "awaiting_confirmation" ||
    summary.confirmation_required
  ) {
    return "awaiting_confirmation";
  }
  if (
    summary.manifest_status === "partial" ||
    summary.availability === "partial" ||
    summary.failed_asset_count > 0
  ) {
    return "partial";
  }
  if (summary.manifest_status === "not_applicable") return "not_applicable";
  if (
    summary.manifest_status === "pending" ||
    summary.manifest_status === "draft"
  ) {
    return "pending";
  }
  if (
    summary.manifest_status === "deleted" ||
    summary.availability === "deleted"
  ) {
    return "deleted";
  }
  if (summary.availability === "legacy") return "legacy";
  if (summary.availability === "unavailable") return "unavailable";
  if (
    summary.availability === "unknown" ||
    summary.manifest_status === "unknown"
  ) {
    return "unknown";
  }
  if (summary.subject_count === 0 && summary.inference_executed === true) {
    return "empty";
  }
  if (summary.subject_count === 0) return "unavailable";
  return "ready";
}

function stateForAbsentSummary(
  availabilityHint?: string | null,
  manifestStatusHint?: string | null,
): IdentitySectionState {
  const status = manifestStatusHint?.toLowerCase();
  const availability = availabilityHint?.toLowerCase();

  if (status === "not_applicable" || availability === "not_applicable") {
    return "not_applicable";
  }
  if (
    status === "pending" ||
    status === "draft" ||
    availability === "pending"
  ) {
    return "pending";
  }
  if (status === "awaiting_confirmation") return "awaiting_confirmation";
  if (status === "failed_safe") return "failed_safe";
  if (status === "partial" || availability === "partial") return "partial";
  if (status === "deleted" || availability === "deleted") return "deleted";
  if (availability === "legacy" || availability === "not_recorded") {
    return "legacy";
  }
  if (availability === "unavailable") return "unavailable";
  return "unknown";
}

function rankSubjects(subjects: IdentitySummaryV1["subjects"]) {
  return subjects
    .map((subject, index) => ({ subject, index }))
    .sort((left, right) => {
      const resolutionDelta =
        (resolutionPriority[left.subject.resolution_status] ?? 2) -
        (resolutionPriority[right.subject.resolution_status] ?? 2);
      if (resolutionDelta !== 0) return resolutionDelta;
      const confidenceDelta =
        (confidencePriority[left.subject.confidence] ?? 2) -
        (confidencePriority[right.subject.confidence] ?? 2);
      return confidenceDelta || left.index - right.index;
    })
    .map(({ subject }) => subject);
}

function safeReasonLabels(reasons: readonly string[]) {
  const labels = reasons.slice(0, 8).map((reason) => {
    if (!SAFE_REASON_CODE.test(reason)) return "Unknown reason";
    return reason.replaceAll("_", " ");
  });
  if (reasons.length > labels.length) labels.push("Additional reasons omitted");
  return Array.from(new Set(labels));
}

export function buildIdentitySectionModel(
  rawSummary: unknown,
  options: {
    availabilityHint?: string | null;
    manifestStatusHint?: string | null;
  } = {},
): IdentitySectionModel {
  if (rawSummary == null) {
    return {
      state: stateForAbsentSummary(
        options.availabilityHint,
        options.manifestStatusHint,
      ),
      summary: null,
      visibleSubjects: [],
      omittedSubjectCount: 0,
      safeReasons: [],
    };
  }

  try {
    const summary = parseIdentitySummary(rawSummary);
    if (summary.subject_count < summary.subjects.length) {
      throw new Error("identity_summary_count_inconsistent");
    }
    const rankedSubjects = rankSubjects(summary.subjects);
    const visibleSubjects = rankedSubjects.slice(
      0,
      MAX_VISIBLE_IDENTITY_SUMMARIES,
    );
    return {
      state: stateForSummary(summary),
      summary,
      visibleSubjects,
      omittedSubjectCount: Math.max(
        0,
        summary.subject_count - visibleSubjects.length,
      ),
      safeReasons: safeReasonLabels(summary.reasons),
    };
  } catch {
    return {
      state: "invalid",
      summary: null,
      visibleSubjects: [],
      omittedSubjectCount: 0,
      safeReasons: [],
    };
  }
}
